/**
 * GutenBricks Frontend Entrypoint
 *
 * Handles frontend-specific functionality for GutenBricks blocks.
 * Responsible for:
 * - Dynamic content loading and initialization
 * - FOUC (Flash of Unstyled Content) prevention
 * - Interactive block initialization (tabs, accordions, sliders)
 *
 * @module entrypoints/frontend
 * @since 2.0.0
 */

import '../types';
import { initNestedInnerBlockAccordions } from '../utils/accordionItems';

// ============================================================================
// Constants
// ============================================================================

/** CSS class names used by GutenBricks */
const CSS_CLASSES = {
    BLOCK: 'gbrx-block',
    TABS: 'gbrx-tabs',
    ACCORDION: 'gbrx-accordion',
    SLIDER: 'gbrx-slider',
    NO_JS: 'gbrx-no-js',
    JS: 'gbrx-js',
    ACTIVE: 'is-active',
    OPEN: 'is-open',
} as const;

/** Data attributes used for interactive elements */
const DATA_ATTRIBUTES = {
    TAB: 'data-tab',
    TAB_PANEL: 'data-tab-panel',
    ACCORDION_TRIGGER: 'data-accordion-trigger',
    ACCORDION_CONTENT: 'data-accordion-content',
    FOUC_HIDDEN: 'data-fouc-hidden',
} as const;

/** Custom event names */
const EVENTS = {
    CONTENT_LOADED: 'gutenbricks:content-loaded',
    BLOCK_READY: 'gutenbricks:block-ready',
    SLIDER_READY: 'gutenbricks:slider-ready',
} as const;

// ============================================================================
// Types
// ============================================================================

/** Custom event detail for content loaded events */
interface ContentLoadedDetail {
    readonly target?: HTMLElement;
}

/** Custom event with typed detail */
interface TypedCustomEvent<T> extends CustomEvent {
    readonly detail: T;
}

// ============================================================================
// Dynamic Content
// ============================================================================

/**
 * Initialize dynamic content handling.
 *
 * Listens for AJAX content loads and processes new GutenBricks blocks.
 */
function initDynamicContent(): void {
    document.addEventListener(EVENTS.CONTENT_LOADED, handleContentLoaded);
}

/**
 * Handle content loaded event.
 *
 * @param event - Custom event with content target
 */
function handleContentLoaded(event: Event): void {
    const customEvent = event as TypedCustomEvent<ContentLoadedDetail>;
    const target = customEvent.detail?.target;

    if (target instanceof HTMLElement) {
        processNewContent(target);
    }
}

/**
 * Process newly loaded content.
 *
 * Finds GutenBricks blocks in the container and dispatches ready events.
 *
 * @param container - Container element with new content
 */
function processNewContent(container: HTMLElement): void {
    const blocks = container.querySelectorAll<HTMLElement>(`.${CSS_CLASSES.BLOCK}`);

    blocks.forEach((block) => {
        dispatchBlockReady(block);
    });

    initNestedInnerBlockAccordions(container);
}

/**
 * Dispatch block ready event.
 *
 * @param block - Block element that is ready
 */
function dispatchBlockReady(block: HTMLElement): void {
    block.dispatchEvent(
        new CustomEvent(EVENTS.BLOCK_READY, {
            bubbles: true,
            detail: { block },
        })
    );
}

// ============================================================================
// FOUC Fix
// ============================================================================

/**
 * Initialize FOUC (Flash of Unstyled Content) fix.
 *
 * Removes no-js class and shows hidden blocks after styles are ready.
 */
function initFoucFix(): void {
    const config = window.gutenbricksConfig;

    if (!config?.foucFix) {
        return;
    }

    // Update document classes
    document.documentElement.classList.remove(CSS_CLASSES.NO_JS);
    document.documentElement.classList.add(CSS_CLASSES.JS);

    // Show blocks that were hidden for FOUC prevention
    showFoucHiddenBlocks();
}

/**
 * Show blocks that were hidden for FOUC prevention.
 */
function showFoucHiddenBlocks(): void {
    const blocks = document.querySelectorAll<HTMLElement>(
        `.${CSS_CLASSES.BLOCK}[${DATA_ATTRIBUTES.FOUC_HIDDEN}]`
    );

    blocks.forEach((block) => {
        block.removeAttribute(DATA_ATTRIBUTES.FOUC_HIDDEN);
    });
}

// ============================================================================
// Interactive Blocks - Tabs
// ============================================================================

/**
 * Initialize tab blocks.
 *
 * Sets up click handlers for tab navigation.
 */
function initTabs(): void {
    const tabContainers = document.querySelectorAll<HTMLElement>(`.${CSS_CLASSES.TABS}`);

    tabContainers.forEach(initTabContainer);
}

/**
 * Initialize a single tab container.
 *
 * @param container - Tab container element
 */
function initTabContainer(container: HTMLElement): void {
    const tabs = container.querySelectorAll<HTMLElement>(`[${DATA_ATTRIBUTES.TAB}]`);
    const panels = container.querySelectorAll<HTMLElement>(`[${DATA_ATTRIBUTES.TAB_PANEL}]`);

    tabs.forEach((tab) => {
        tab.addEventListener('click', () => handleTabClick(tab, tabs, panels));
    });
}

/**
 * Handle tab click event.
 *
 * @param clickedTab - Tab that was clicked
 * @param allTabs - All tabs in the container
 * @param panels - All panels in the container
 */
function handleTabClick(
    clickedTab: HTMLElement,
    allTabs: NodeListOf<HTMLElement>,
    panels: NodeListOf<HTMLElement>
): void {
    const targetId = clickedTab.getAttribute(DATA_ATTRIBUTES.TAB);

    // Update tab active states
    allTabs.forEach((tab) => {
        tab.classList.remove(CSS_CLASSES.ACTIVE);
    });
    clickedTab.classList.add(CSS_CLASSES.ACTIVE);

    // Update panel visibility
    panels.forEach((panel) => {
        const panelId = panel.getAttribute(DATA_ATTRIBUTES.TAB_PANEL);
        panel.classList.toggle(CSS_CLASSES.ACTIVE, panelId === targetId);
    });
}

// ============================================================================
// Interactive Blocks - Accordions
// ============================================================================

/**
 * Initialize accordion blocks.
 *
 * Sets up click handlers for accordion triggers.
 */
function initAccordions(): void {
    const accordions = document.querySelectorAll<HTMLElement>(`.${CSS_CLASSES.ACCORDION}`);

    accordions.forEach(initAccordionContainer);
}

/**
 * Initialize a single accordion container.
 *
 * @param accordion - Accordion container element
 */
function initAccordionContainer(accordion: HTMLElement): void {
    const triggers = accordion.querySelectorAll<HTMLElement>(
        `[${DATA_ATTRIBUTES.ACCORDION_TRIGGER}]`
    );

    triggers.forEach((trigger) => {
        trigger.addEventListener('click', () => handleAccordionClick(trigger, accordion));
    });
}

/**
 * Handle accordion trigger click.
 *
 * @param trigger - Trigger element that was clicked
 * @param accordion - Parent accordion container
 */
function handleAccordionClick(trigger: HTMLElement, accordion: HTMLElement): void {
    const targetId = trigger.getAttribute(DATA_ATTRIBUTES.ACCORDION_TRIGGER);

    if (!targetId) {
        return;
    }

    const content = accordion.querySelector<HTMLElement>(
        `[${DATA_ATTRIBUTES.ACCORDION_CONTENT}="${targetId}"]`
    );

    if (!content) {
        return;
    }

    const isOpen = content.classList.contains(CSS_CLASSES.OPEN);
    content.classList.toggle(CSS_CLASSES.OPEN, !isOpen);
    trigger.setAttribute('aria-expanded', String(!isOpen));
}

// ============================================================================
// Interactive Blocks - Sliders
// ============================================================================

/**
 * Initialize slider blocks.
 *
 * Dispatches ready events for custom slider initialization.
 * Note: Sliders are typically handled by Bricks' native slider element.
 */
function initSliders(): void {
    const sliders = document.querySelectorAll<HTMLElement>(`.${CSS_CLASSES.SLIDER}`);

    sliders.forEach((slider) => {
        slider.dispatchEvent(
            new CustomEvent(EVENTS.SLIDER_READY, {
                bubbles: true,
                detail: { slider },
            })
        );
    });
}

// ============================================================================
// Interactive Blocks - Main
// ============================================================================

/**
 * Initialize all interactive blocks.
 */
function initInteractiveBlocks(): void {
    initTabs();
    initAccordions();
    initNestedInnerBlockAccordions();
    initSliders();
}

// ============================================================================
// Initialization
// ============================================================================

/**
 * Initialize frontend functionality.
 *
 * Sets up dynamic content handling, FOUC fix, and interactive blocks.
 */
function initFrontend(): void {
    initDynamicContent();
    initFoucFix();
    initInteractiveBlocks();
}

// ============================================================================
// Entry Point
// ============================================================================

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFrontend);
} else {
    initFrontend();
}
